import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, Printer, Upload, Plus, Pencil, ArrowUp, ArrowDown, Trash, FileText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  usePatients, usePatientVisits, useVisit, useClinicDoctors, useDischargeSummary, useDischargeSectionTemplates,
  type DischargeMedicineRow,
} from "@/lib/queries";
import {
  useCreateDischargeSummary, useUpdateDischargeSummary,
} from "@/lib/mutations";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import { DischargeEditMedicineDialog } from "@/features/dischargesummaries/DischargeEditMedicineDialog";
import { createDischargeSummaryPdf } from "@/lib/pdf/dischargeSummaryPdf";
import { savePdfToR2 } from "@/lib/r2";
import { printPdf } from "@/lib/print";
import { printCompositionPref } from "@/lib/rxPrint";
import { fmtDate, fmtDateTime, dateIST, fmtDatePdf } from "@emr/shared/india";
import { DISCHARGE_HEADINGS, type DischargeHeading } from "@emr/shared/constants";
import { toast } from "sonner";

// DAMA resets both show_* flags off; the other headings default them on.
function headingDefaults(h: DischargeHeading): { showPrescription: boolean; showReviewDate: boolean } {
  return h === "Discharge Against Medical Advice"
    ? { showPrescription: false, showReviewDate: false }
    : { showPrescription: true, showReviewDate: true };
}

// Default review sentence auto-generated when a review date is picked
// (2026-10-16). The user can edit the text freely in the review box; the
// stored text is authoritative for the PDF. Not exported — a non-component
// export breaks React Fast Refresh for this file.
function defaultReviewText(reviewDate: string): string {
  return `Please review in PS Neurocentre on ${fmtDatePdf(reviewDate)}. In case of any emergency please contact your local doctor or during working hours please call 88037 88027 or 0462-4220087`;
}

// datetime-local value (IST) from an ISO timestamp — for form hydration.
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // IST = UTC+5:30 — build the local-input string manually so the value is
  // stable regardless of the browser's timezone.
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 16);
}

interface DraftMedicine {
  key: string;
  drugId: string | null;
  name: string;
  composition: string | null;
  formulation: string | null;
  strength: string | null;
  frequency: string | null;
  taper: import("@emr/shared").TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
}

function medicineToDraft(m: Partial<DischargeMedicineRow> & { id?: string }): DraftMedicine {
  return {
    key: crypto.randomUUID(),
    drugId: m.drugId ?? null,
    name: m.name ?? "",
    composition: m.composition ?? null,
    formulation: m.formulation ?? null,
    strength: m.strength ?? null,
    frequency: m.frequency ?? null,
    taper: m.taper ?? null,
    duration: m.duration ?? null,
    instructions: m.instructions ?? null,
  };
}

export function DischargeSummaryEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: existing, isLoading } = useDischargeSummary(id);
  const { data: clinicDoctorsData } = useClinicDoctors();
  const clinicDoctors: any[] = (clinicDoctorsData as any)?.rows ?? (Array.isArray(clinicDoctorsData) ? clinicDoctorsData : []) ?? [];
  const { data: templatesData } = useDischargeSectionTemplates();
  const templates = templatesData?.rows ?? [];

  const [heading, setHeading] = useState<DischargeHeading>("Discharge Summary");
  const [patientId, setPatientId] = useState("");
  const [patientQuery, setPatientQuery] = useState("");
  const [visitId, setVisitId] = useState("");
  const [admissionAt, setAdmissionAt] = useState("");
  const [dischargeAt, setDischargeAt] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [history, setHistory] = useState("");
  const [examination, setExamination] = useState("");
  const [investigations, setInvestigations] = useState("");
  const [treatmentInHospital, setTreatmentInHospital] = useState("");
  const [courseInHospital, setCourseInHospital] = useState("");
  const [generalAdvice, setGeneralAdvice] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [showPrescription, setShowPrescription] = useState(true);
  const [showReviewDate, setShowReviewDate] = useState(true);
  const [doctorId, setDoctorId] = useState("");
  const [medicines, setMedicines] = useState<DraftMedicine[]>([]);
  const [updatedAt, setupdatedAt] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // Edit-medicine dialog state (add = null row, edit = existing draft row).
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const create = useCreateDischargeSummary();
  const update = useUpdateDischargeSummary(id ?? "");

  const { data: patientsData } = usePatients(patientQuery, 1, 10);
  const patientOptions = patientsData?.rows ?? [];
  const patientSearchRef = useRef<HTMLInputElement>(null);
  const { highlight: ptHighlight, highlightRef: ptHighlightRef, onKeyDown: onPtSearchKeyDown } = useDropdownNav({
    count: patientQuery.length >= 3 ? patientOptions.length : 0,
    onSelect: (i) => {
      if (patientOptions[i]) {
        const p = patientOptions[i];
        setPatientId(p.id);
        setPatientQuery([p.salutation, p.name].filter(Boolean).join(" ") + " · " + p.clinicUhid);
        setVisitId("");
      }
    },
    inputRef: patientSearchRef,
  });

  const { data: patientVisitsData } = usePatientVisits(patientId || undefined);
  const patientVisits = patientVisitsData?.rows ?? [];
  const { data: visitData } = useVisit(visitId || undefined);

  // Default the visit selector to the most recent visit when a patient is
  // picked (and none is chosen yet).
  useEffect(() => {
    if (patientId && !visitId && patientVisits.length > 0) setVisitId(patientVisits[0].id);
    if (!patientId && visitId) setVisitId("");
  }, [patientId, patientVisits]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate from an existing summary.
  useEffect(() => {
    if (existing?.summary) {
      const s = existing.summary;
      setHeading(s.heading);
      setPatientId(s.patientId);
      setPatientQuery(s.patientName ? `${s.patientName} · ${s.clinicUhid}` : "");
      setVisitId(s.visitId ?? "");
      setAdmissionAt(isoToLocalInput(s.admissionAt));
      setDischargeAt(isoToLocalInput(s.dischargeAt));
      setDiagnosis(s.diagnosis ?? "");
      setHistory(s.history ?? "");
      setExamination(s.examination ?? "");
      setInvestigations(s.investigations ?? "");
      setTreatmentInHospital(s.treatmentInHospital ?? "");
      setCourseInHospital(s.courseInHospital ?? "");
      setGeneralAdvice(s.generalAdvice ?? "");
      setReviewDate(s.reviewDate ?? "");
      setReviewText(s.reviewText ?? "");
      setShowPrescription(s.showPrescription);
      setShowReviewDate(s.showReviewDate);
      setDoctorId(s.doctorId ?? "");
      setMedicines((existing.medicines ?? []).map(medicineToDraft));
      setupdatedAt(s.updatedAt);
    }
  }, [existing]);

  // Heading change resets the show_* flags to the heading's defaults.
  const onHeadingChange = (h: string) => {
    if (h === "Discharge Summary" || h === "Discharge At Request" || h === "Discharge Against Medical Advice") {
      setHeading(h);
      const d = headingDefaults(h);
      setShowPrescription(d.showPrescription);
      setShowReviewDate(d.showReviewDate);
    }
  };

  // Import from the selected visit: diagnosis + medicines (non-destructive —
  // only on explicit click).
  const importFromVisit = () => {
    if (!visitData) { toast.error("Select a visit first."); return; }
    const note = visitData.note;
    if (note?.diagnosis) setDiagnosis(note.diagnosis);
    const rxMeds = visitData.prescriptions?.flatMap((p: any) => p.medicines ?? []) ?? [];
    if (rxMeds.length > 0) {
      setMedicines(rxMeds.map((m: any) => medicineToDraft(m)));
    }
    toast.success(`Imported from visit of ${fmtDate(visitData.visit?.checkInAt ?? new Date())}`);
  };

  // Insert a section template — REPLACES the field (confirm when non-empty).
  const insertTemplate = (section: "History" | "Examination", content: string, title: string) => {
    const current = section === "History" ? history : examination;
    const apply = () => {
      if (section === "History") setHistory(content);
      else setExamination(content);
    };
    if (current.trim()) {
      // eslint-disable-next-line no-alert -- lightweight inline confirm
      if (window.confirm(`Insert "${title}"? This will REPLACE the current ${section} text.`)) apply();
    } else {
      apply();
    }
  };

  const buildPayload = () => ({
    patientId,
    visitId: visitId || null,
    heading,
    admissionAt: admissionAt || null,
    dischargeAt: dischargeAt || null,
    diagnosis: diagnosis || null,
    history: history || null,
    examination: examination || null,
    investigations: investigations || null,
    treatmentInHospital: treatmentInHospital || null,
    courseInHospital: courseInHospital || null,
    generalAdvice: generalAdvice || null,
    reviewDate: reviewDate || null,
    reviewText: reviewText || null,
    showPrescription,
    showReviewDate,
    doctorId: doctorId || null,
    medicines: medicines.map((m, i) => ({
      drugId: m.drugId,
      name: m.name,
      composition: m.composition,
      formulation: m.formulation,
      strength: m.strength,
      frequency: m.frequency,
      taper: m.taper,
      duration: m.duration,
      instructions: m.instructions,
      serialNo: i + 1,
    })),
  });

  const selectedPatient = existing?.summary
    ?? (patientId ? patientOptions.find((p: any) => p.id === patientId) : undefined);
  const selectedDoctor = clinicDoctors.find((d: any) => d.id === doctorId);

  const buildPdfData = () => ({
    heading,
    patientName: (existing?.summary?.patientName
      ?? (selectedPatient as any)?.name
      ?? "Patient"),
    clinicUhid: existing?.summary?.clinicUhid ?? (selectedPatient as any)?.clinicUhid ?? "",
    patientDob: existing?.summary?.patientDob ?? (selectedPatient as any)?.dob ?? null,
    patientGender: existing?.summary?.patientGender ?? (selectedPatient as any)?.gender ?? null,
    patientPhone: existing?.summary?.patientPhone ?? (selectedPatient as any)?.phone ?? null,
    patientAddress: existing?.summary?.patientAddress ?? (selectedPatient as any)?.address ?? null,
    admissionAt: admissionAt || null,
    dischargeAt: dischargeAt || null,
    diagnosis: diagnosis || null,
    history: history || null,
    examination: examination || null,
    investigations: investigations || null,
    treatmentInHospital: treatmentInHospital || null,
    courseInHospital: courseInHospital || null,
    generalAdvice: generalAdvice || null,
    reviewDate: reviewDate || null,
    reviewText: reviewText || null,
    showPrescription,
    showReviewDate,
    medicines: medicines.map((m, i) => ({
      serialNo: i + 1,
      name: m.name,
      composition: m.composition,
      strength: m.strength,
      frequency: m.frequency,
      taper: m.taper,
      duration: m.duration,
      instructions: m.instructions,
    })),
    showComposition: printCompositionPref(),
    doctorName: selectedDoctor?.name ?? null,
    doctorQualifications: selectedDoctor?.qualifications ?? null,
    doctorRegNumber: selectedDoctor?.regNumber ?? null,
  });

  const saveToDb = async (): Promise<string | null> => {
    if (!patientId) { toast.error("Select a patient first."); return null; }
    const payload = buildPayload();
    if (id) {
      await update.mutateAsync({ ...payload, updatedAt });
      return id;
    }
    const row = await create.mutateAsync(payload) as { id: string };
    return row.id;
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (savedId && !id) navigate(`/discharge-summaries/${savedId}`);
    } catch { /* mutation toast handles */ } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (!savedId) return;
      const doc = await createDischargeSummaryPdf(buildPdfData());
      printPdf(doc);
    } catch {
      toast.error("PDF generation failed — could not print.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveToCloud = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (!savedId) return;
      const doc = await createDischargeSummaryPdf(buildPdfData());
      // Replace-in-place (2026-10-16): when the summary already has a cloud
      // copy, overwrite the SAME R2 key instead of minting a new one — the
      // old PDF is replaced by the newly generated one, no orphans.
      const existingKey = existing?.summary?.r2Key ?? null;
      const fullName = (existing?.summary?.patientName ?? (selectedPatient as any)?.name ?? "Patient");
      const fileName = `${fullName}_${dateIST().slice(0, 10)}_${existing?.summary?.clinicUhid ?? (selectedPatient as any)?.clinicUhid ?? "UHID"}_${savedId.slice(0, 6)}`;
      const r2Key = existingKey
        ? await savePdfToR2(doc, "discharge-summaries", fileName, patientId, visitId || undefined, existingKey)
        : await savePdfToR2(doc, "discharge-summaries", fileName, patientId, visitId || undefined);
      await update.mutateAsync({ savedToR2: true, r2Key, updatedAt });
      toast.success("Saved to cloud");
    } catch {
      toast.error("Cloud save failed.");
    } finally {
      setBusy(false);
    }
  };

  // Review date change: auto-generate the default review sentence whenever
  // the date changes (2026-10-16). If the user has customized the text, the
  // customization is preserved — only the date inside the default template
  // is refreshed when the text still matches the default pattern.
  const onReviewDateChange = (v: string) => {
    setReviewDate(v);
    if (!v) return;
    setReviewText((prev) => {
      // Regenerate if empty or if the previous text was the auto-generated
      // default for the previous date (i.e. not user-customized).
      if (!prev.trim() || /^Please review in PS Neurocentre on .+\. In case of any emergency/.test(prev)) {
        return defaultReviewText(v);
      }
      return prev;
    });
  };

  // Medicine row actions.
  const addMedicine = () => { setEditIdx(null); setDialogOpen(true); };
  const editMedicine = (idx: number) => { setEditIdx(idx); setDialogOpen(true); };
  const moveMedicine = (idx: number, dir: -1 | 1) => {
    setMedicines((rows) => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const removeMedicine = (idx: number) => {
    setMedicines((rows) => rows.filter((_, i) => i !== idx));
  };

  const dialogMedicine: Partial<DischargeMedicineRow> | null = editIdx !== null
    ? medicines[editIdx]
      ? {
        id: medicines[editIdx].key,
        dischargeSummaryId: id ?? "",
        drugId: medicines[editIdx].drugId,
        serialNo: editIdx + 1,
        name: medicines[editIdx].name,
        composition: medicines[editIdx].composition,
        formulation: medicines[editIdx].formulation,
        strength: medicines[editIdx].strength,
        frequency: medicines[editIdx].frequency,
        taper: medicines[editIdx].taper,
        duration: medicines[editIdx].duration,
        instructions: medicines[editIdx].instructions,
      }
      : null
    : null;

  const onDialogSave = (m: Partial<DischargeMedicineRow>) => {
    if (editIdx !== null) {
      setMedicines((rows) => rows.map((r, i) => (i === editIdx ? { ...r, ...m, key: r.key } : r)));
    } else {
      setMedicines((rows) => [...rows, medicineToDraft({ ...m } as Partial<DischargeMedicineRow>)]);
    }
    setDialogOpen(false);
  };

  const historyTemplates = templates.filter((t) => t.section === "History");
  const examinationTemplates = templates.filter((t) => t.section === "Examination");

  if (isLoading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/discharge-summaries")}><ArrowLeft size={18} /></Button>
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">
            {id ? "Edit Discharge Summary" : "New Discharge Summary"}
          </h1>
        </div>
      </div>

      {/* ---- Heading + patient + visit import ---- */}
      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label>Document heading *</Label>
            <Select value={heading} onValueChange={onHeadingChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DISCHARGE_HEADINGS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Patient *</Label>
            <Input
              ref={patientSearchRef}
              value={patientQuery}
              onChange={(e) => { setPatientQuery(e.target.value); setPatientId(""); setVisitId(""); }}
              onKeyDown={onPtSearchKeyDown}
              placeholder="Type name/phone/UHID to search (3+ chars)…"
            />
            {patientOptions.length > 0 && (
              <div className="visible-scrollbar mt-1 max-h-40 overscroll-contain overflow-auto rounded border border-cream-200 bg-cream-100">
                {patientOptions.map((p: any, idx: number) => (
                  <button
                    key={p.id}
                    type="button"
                    ref={idx === ptHighlight ? ptHighlightRef : undefined}
                    className={`block w-full px-3 py-1 text-left text-sm hover:bg-cream-200 ${
                      idx === ptHighlight ? "bg-cream-200" : ""
                    }`}
                    onClick={() => {
                      setPatientId(p.id);
                      setPatientQuery([p.salutation, p.name].filter(Boolean).join(" ") + " · " + p.clinicUhid);
                      setVisitId("");
                    }}
                  >
                    {[p.salutation, p.name].filter(Boolean).join(" ")} · {p.clinicUhid} · {p.phone}
                  </button>
                ))}
              </div>
            )}
            {patientId && <p className="mt-1 text-xs text-green-700">Selected: {patientId.slice(0, 8)}…</p>}
          </div>
          <div>
            <Label>Import from visit {patientId && patientVisits.length === 0 ? "(no visits found)" : ""}</Label>
            <div className="flex gap-2">
              <Select
                value={visitId}
                onValueChange={(v) => setVisitId(v)}
                disabled={!patientId || patientVisits.length === 0}
              >
                <SelectTrigger><SelectValue placeholder={patientId ? "Select a visit" : "Select a patient first"} /></SelectTrigger>
                <SelectContent>
                  {patientVisits.map((v: any) => (
                    <SelectItem key={v.id} value={v.id}>
                      {fmtDate(v.checkInAt)} · {v.diagnosis ? v.diagnosis.slice(0, 40) : v.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={importFromVisit} disabled={!visitId}>
                <FileText size={14} className="mr-1" /> Import
              </Button>
            </div>
            {visitId && visitData?.note?.diagnosis && (
              <p className="mt-1 text-xs"><span className="font-medium">Visit diagnosis:</span> {visitData.note.diagnosis}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date of admission</Label>
              <Input type="datetime-local" value={admissionAt} onChange={(e) => setAdmissionAt(e.target.value)} />
            </div>
            <div>
              <Label>Date of discharge</Label>
              <Input type="datetime-local" value={dischargeAt} onChange={(e) => setDischargeAt(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---- Clinical sections ---- */}
      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Clinical Content</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Diagnosis</Label>
            <Textarea value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} rows={2} placeholder="One diagnosis per line (numbered on the PDF)" />
          </div>

          {/* History with template insert */}
          <div>
            <div className="flex items-center justify-between">
              <Label>History</Label>
              {historyTemplates.length > 0 && (
                <Select onValueChange={(v) => {
                  const t = historyTemplates.find((x) => x.id === v);
                  if (t) insertTemplate("History", t.content ?? "", t.title);
                }}>
                  <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="Insert template…" /></SelectTrigger>
                  <SelectContent>
                    {historyTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Textarea value={history} onChange={(e) => setHistory(e.target.value)} rows={4} />
          </div>

          {/* Examination with template insert */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Examination</Label>
              {examinationTemplates.length > 0 && (
                <Select onValueChange={(v) => {
                  const t = examinationTemplates.find((x) => x.id === v);
                  if (t) insertTemplate("Examination", t.content ?? "", t.title);
                }}>
                  <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="Insert template…" /></SelectTrigger>
                  <SelectContent>
                    {examinationTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Textarea value={examination} onChange={(e) => setExamination(e.target.value)} rows={4} />
          </div>

          <div>
            <Label>Investigations</Label>
            <Textarea value={investigations} onChange={(e) => setInvestigations(e.target.value)} rows={3} />
          </div>
          <div>
            <Label>Treatment in Hospital</Label>
            <Textarea value={treatmentInHospital} onChange={(e) => setTreatmentInHospital(e.target.value)} rows={3} />
          </div>
          <div>
            <Label>Course in Hospital</Label>
            <Textarea value={courseInHospital} onChange={(e) => setCourseInHospital(e.target.value)} rows={3} />
          </div>
        </CardContent>
      </Card>

      {/* ---- Advice on discharge: general advice + prescription ---- */}
      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Advice on Discharge</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>General advice</Label>
            <Textarea value={generalAdvice} onChange={(e) => setGeneralAdvice(e.target.value)} rows={3} placeholder="e.g. Please go to the nearest local hospital if symptoms worsen." />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-rx"
                checked={showPrescription}
                onCheckedChange={(v) => setShowPrescription(v === true)}
              />
              <Label htmlFor="show-rx" className="text-sm">Show prescription on PDF</Label>
            </div>
            {showPrescription && (
              <Button variant="outline" size="sm" onClick={addMedicine}><Plus size={14} className="mr-1" /> Add medicine</Button>
            )}
          </div>

          {showPrescription ? (
            medicines.length === 0 ? (
              <p className="text-sm text-muted">No medicines added. Use "Add medicine" or import from a visit.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Drug</TableHead>
                    <TableHead>Instructions</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {medicines.map((m, i) => (
                    <TableRow key={m.key} className="even:bg-cream-50/50">
                      <TableCell>{i + 1}</TableCell>
                      <TableCell className="font-medium">
                        {m.name}
                        {m.composition && <span className="block text-xs text-muted">({m.composition})</span>}
                      </TableCell>
                      <TableCell className="text-xs">{m.instructions ?? "—"}</TableCell>
                      <TableCell className="text-xs">{m.frequency ?? "—"}</TableCell>
                      <TableCell className="text-xs">{m.duration ?? "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-0.5">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveMedicine(i, -1)} disabled={i === 0}><ArrowUp size={13} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveMedicine(i, 1)} disabled={i === medicines.length - 1}><ArrowDown size={13} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editMedicine(i)}><Pencil size={13} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeMedicine(i)}><Trash size={13} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <p className="rounded-md border border-dashed border-cream-300 p-3 text-sm text-muted dark:border-dark-600">
              Prescription is hidden on the PDF (typical for Discharge Against Medical Advice). Medicines are kept — re-check to include.
              {medicines.length > 0 && <> {medicines.length} medicine{medicines.length > 1 ? "s" : ""} preserved.</>}
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-review"
                  checked={showReviewDate}
                  onCheckedChange={(v) => setShowReviewDate(v === true)}
                />
                <Label htmlFor="show-review" className="text-sm">Show review on PDF</Label>
              </div>
              <Input
                type="date"
                value={reviewDate}
                onChange={(e) => onReviewDateChange(e.target.value)}
                disabled={!showReviewDate}
              />
              <div>
                <Label className="text-xs">Review text (printed below the prescription)</Label>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  disabled={!showReviewDate}
                  rows={3}
                  placeholder="Pick a review date to auto-generate, or type the review advice here."
                />
              </div>
            </div>
            <div>
              <Label>Doctor (signature)</Label>
              <Select value={doctorId} onValueChange={(v) => setDoctorId(v)}>
                <SelectTrigger><SelectValue placeholder="Select clinic doctor" /></SelectTrigger>
                <SelectContent>
                  {clinicDoctors.map((d: any) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---- Actions ---- */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleSave} disabled={busy || !patientId} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Save size={14} className="mr-1" /> Save</Button>
        <Button variant="outline" onClick={handlePrint} disabled={busy || !patientId}><Printer size={14} className="mr-1" /> Print</Button>
        <Button variant="outline" onClick={handleSaveToCloud} disabled={busy || !patientId}><Upload size={14} className="mr-1" /> Save to cloud</Button>
        <Button variant="ghost" onClick={() => navigate("/discharge-summaries")}>Close</Button>
      </div>

      {existing?.summary?.savedToR2 && (
        <p className="text-xs text-muted">Cloud copy saved {existing.summary.r2Key ? `(${existing.summary.r2Key.split("/").pop()})` : ""}.</p>
      )}

      <DischargeEditMedicineDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        medicine={dialogMedicine as never}
        onSave={onDialogSave}
        onDelete={() => { if (editIdx !== null) removeMedicine(editIdx); setDialogOpen(false); }}
      />
    </div>
  );
}
